import { PAYMENT_ACCOUNT_KEYS } from '@metycle/shared';
import { z } from 'zod';

const optionalCompanyText = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
  z.string().min(1).max(255).optional(),
);

const optionalRemarks = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
  z.string().max(2000).optional(),
);

// Single-line free text (commodity name, payment term). Blank clears to undefined.
const optionalShortText = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
  z.string().max(255).optional(),
);

// The two trade types a Draft invoice can be re-typed between. Mirrors the
// backend update-invoice contract (`invoice_type` accepts these exact strings).
export const RECLASSIFY_TRADE_TYPES = ['Provisional', 'Final Settlement'] as const;
export type ReclassifyTradeType = (typeof RECLASSIFY_TRADE_TYPES)[number];

// Empty string clears to undefined; a supplied reason must be at least 5 chars
// (the backend rejects shorter ones). "Required when the type actually changed"
// can't live here — it needs the original type — so the dialog guards that.
const optionalReclassifyReason = z.preprocess(
  (v) => (typeof v === 'string' && v.trim() === '' ? undefined : v),
  z.string().min(5, 'Give a reason of at least 5 characters').max(2000).optional(),
);

// VAT is optional on the invoice: a blank field (empty string / NaN from an
// untouched number input) settles to 0 so operators can "leave blank if not on
// invoice" without tripping validation.
const optionalVatAmount = z.preprocess(
  (v) =>
    v === '' || v === undefined || v === null || (typeof v === 'number' && Number.isNaN(v)) ? 0 : v,
  z.coerce.number().nonnegative(),
);

// Held as the native date input's YYYY-MM-DD string (and forwarded verbatim on
// the wire); a blank input settles to undefined so the optional supplier
// invoice date can be left empty.
const optionalDate = z.preprocess(
  (v) => (v === '' || v === null ? undefined : v),
  z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected an ISO date in YYYY-MM-DD form')
    .optional(),
);

// Presentational status options — limited to the statuses our system actually
// has today (launchpad-only Pending/Sent/Overdue are intentionally omitted; add
// them later if the backend gains them). The backend does not accept a status
// on the provisional create/edit payload (new invoices are always Draft; status
// otherwise moves through lifecycle actions), so this field is intentionally
// NOT forwarded on submit — see baseWirePayload.
export const PROVISIONAL_STATUS_OPTIONS = ['Draft', 'Paid', 'Cancelled'] as const;

export const PROVISIONAL_SUBMIT_FIELDS = z.object({
  invoice_number: z.string().min(1, 'Invoice number is required'),
  currency: z.enum(['USD', 'EUR']),
  total_amount: z.coerce.number().positive(),
  vat_amount: optionalVatAmount,
  issue_date: optionalDate,
  original_due_date: z.coerce.date(),
  status: z.enum(PROVISIONAL_STATUS_OPTIONS).optional(),
  market_reference_price: z.coerce.number().positive(),
  market_reference_date: z.coerce.date(),
  amount_qt_mt: z.coerce.number().positive(),
  applied_downpayment_amount: z.coerce.number().nonnegative(),
  issuer_company_name: optionalCompanyText,
  company_name: optionalCompanyText,
  item_description: optionalShortText,
  payment_term: optionalShortText,
  remarks: optionalRemarks,
  // Receiving bank account for the PDF payment block (sell side); 'auto' keeps
  // the backend's currency/country routing and maps to null on the wire.
  payment_account_key: z.enum(['auto', ...PAYMENT_ACCOUNT_KEYS]).optional(),
  // Edit-only: the target type chosen in the reclassify control (defaults to
  // the current type, so an unchanged select is a no-op) and its reason.
  reclassify_to_invoice_type: z.enum(RECLASSIFY_TRADE_TYPES).optional(),
  reclassification_reason: optionalReclassifyReason,
});

export type ProvisionalSubmitValues = z.infer<typeof PROVISIONAL_SUBMIT_FIELDS>;
